import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { Signature } from '../../interfaces/signature';
import * as SignatureActions from './signatures.actions';

export const signaturesFeatureKey = 'signatures';

export interface State extends EntityState<Signature> {
  // additional entities state properties
  loading: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  error: any;
}

export const adapter: EntityAdapter<Signature> = createEntityAdapter<Signature>();

export const initialState: State = adapter.getInitialState({
  // additional entity state properties
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  error: null,
});

export const signatureReducer = createReducer(
  initialState,
  on(SignatureActions.loadSignatures, (state) => ({ ...state, loading: true })),
  on(SignatureActions.loadSignaturesSuccess, (state, action) =>
    adapter.upsertMany(action.signatures, { ...state, loading: false })
  ),
  on(SignatureActions.loadSignaturesFailure, (state, action) => ({ ...state, error: action.error, loading: false })),
  on(SignatureActions.createSignature, (state, action) => ({ action, ...state, creating: true })),
  on(SignatureActions.createSignatureSuccess, (state, action) => {
    let newState = state;
    if (action.signature.default) {
      newState = adapter.map((signature) => ({ ...signature, default: false }), state);
    }
    return adapter.upsertOne(action.signature, { ...newState, creating: false });
  }),
  on(SignatureActions.createSignatureFailure, (state, action) => ({ ...state, error: action.error, creating: false })),
  on(SignatureActions.updateSignature, (state, action) => ({ action, ...state, updating: true })),
  on(SignatureActions.updateSignatureSuccess, (state, action) => {
    let newState = state;
    if (action.signature.default) {
      newState = adapter.map((signature) => ({ ...signature, default: false }), state);
    }
    return adapter.upsertOne(action.signature, { ...newState, updating: false });
  }),
  on(SignatureActions.updateSignatureFailure, (state, action) => ({ ...state, error: action.error, updating: false })),
  on(SignatureActions.deleteSignature, (state) => ({ ...state, deleting: true })),
  on(SignatureActions.deleteSignatureSuccess, (state, action) =>
    adapter.removeOne(action.id, { ...state, deleting: true })
  ),
  on(SignatureActions.deleteSignatureFailure, (state, action) => ({ ...state, error: action.error, deleting: true })),
  on(SignatureActions.clearSignatures, (state) => adapter.removeAll(state))
);

export const selectSignatureState = createFeatureSelector<State>(signaturesFeatureKey);

const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors(selectSignatureState);

export const selectSignaturesAll = selectAll;
export const selectSignaturesTotal = selectTotal;
export const selectSignaturesEntities = selectEntities;
export const selectSignaturesIds = selectIds;

export const selectSignaturesLoading = createSelector(selectSignatureState, (state) => state.loading);
export const selectSignaturesDefault = createSelector(selectAll, (signatures) =>
  signatures.find((signature) => signature.default)
);
